/* nplus-leasing-deposit.js — <nplus-leasing-deposit> element.
   Combined leasing inquiry + card deposit: one submit creates a Shopify draft order and a
   Stripe PaymentIntent (cross-linked) via the backend, then confirms the payment.
   Loaded after nplus-leasing-deposit-service.js. Uses Stripe.js (global Stripe) +
   window.nplusLeasingDeposit. */
(function () {
  'use strict';

  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  var BILLING_REQUIRED = ['address1', 'city', 'province', 'zip', 'country'];

  class NplusLeasingDeposit extends HTMLElement {
    connectedCallback() {
      if (this._connected) return; // guard against duplicate listeners on DOM re-insertion
      this._connected = true;
      var cfgEl = this.querySelector('.nplus-leasing-deposit__config');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }

      var self = this;
      this.fields = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-field]'), function (el) {
        self.fields[el.getAttribute('data-nplus-field')] = el;
      });
      this.billing = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-billing]'), function (el) {
        self.billing[el.getAttribute('data-nplus-billing')] = el;
      });
      this.protection = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-protection]'), function (el) {
        self.protection[el.getAttribute('data-nplus-protection')] = el;
      });
      this.consentEl = this.querySelector('[data-nplus-consent]');
      var label = this.querySelector('[data-nplus-consent-label]');
      if (label) label.textContent = this.cfg.consentLabel || 'I consent.';
      this.termsEl = this.querySelector('[data-nplus-terms]');
      this.submitEl = this.querySelector('[data-nplus-submit]');
      this.statusEl = this.querySelector('[data-nplus-status]');

      if (this.submitEl) this.submitEl.addEventListener('click', this.onSubmit.bind(this));

      // Mount the Payment Element when our reveal panel opens. The buybox host only
      // auto-mounts its own 'deposit' panel; setTimeout defers past togglePanel's unhide.
      var host = this.closest('nplus-buybox');
      var reveal = host ? host.querySelector('[data-nplus-reveal="leasing-deposit"]') : null;
      if (reveal) reveal.addEventListener('click', function () { setTimeout(function () { self.ensureStripe(); }, 0); });
    }

    setStatus(msg, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg || '';
      this.statusEl.hidden = !msg;
      this.statusEl.classList.toggle('nplus-bb__status--error', !!isError);
    }
    setBusy(b) { if (this.submitEl) { this.submitEl.disabled = b; this.submitEl.setAttribute('aria-busy', b ? 'true' : 'false'); } }
    t(key, fallback) { return (this.cfg && this.cfg.i18n && this.cfg.i18n[key]) || fallback; }

    showFieldError(key, msg) {
      var el = this.querySelector('[data-nplus-error-for="' + key + '"]');
      if (el) { el.textContent = msg; el.hidden = false; }
    }
    clearFieldErrors() {
      Array.prototype.forEach.call(this.querySelectorAll('[data-nplus-error-for]'), function (e) { e.hidden = true; });
    }

    ensureStripe() {
      if (this._stripeInit) return;
      this._stripeInit = true;
      if (typeof window.Stripe !== 'function' || !window.nplusLeasingDeposit || !this.cfg.publishableKey) {
        this.setStatus(this.t('payment_unavailable', 'Payment is temporarily unavailable.'), true);
        if (this.submitEl) this.submitEl.disabled = true;
        return;
      }
      this.stripe = window.Stripe(this.cfg.publishableKey);
      this.elements = this.stripe.elements({ mode: 'payment', amount: Math.round(Number(this.cfg.amount)), currency: String(this.cfg.currency || '').toLowerCase(), paymentMethodTypes: ['card'], appearance: { theme: 'stripe' } });
      this.paymentElement = this.elements.create('payment');
      // Mount into THIS element's own target — the Lease and Finance tabs each render an
      // <nplus-leasing-deposit>, so a global #id would collide. Falls back to the legacy id.
      this.paymentElement.mount(this.querySelector('[data-nplus-pe]') || '#nplus-payment-element');
      this.cardReady = false;
      var self = this;
      this.paymentElement.on('change', function (e) { self.cardReady = !!e.complete; });
    }

    collect() {
      var f = this.fields, b = this.billing, p = this.protection;
      var val = function (el) { return el ? el.value.trim() : ''; };
      var out = {
        productId: this.cfg.productId,
        variantId: this.cfg.variantId,
        type: this.cfg.type || 'leasing',
        customer: { firstName: val(f.firstName), lastName: val(f.lastName), email: val(f.email), phone: val(f.phone) },
        employer: val(f.employer),
        consent: !!(this.consentEl && this.consentEl.checked),
        currency: this.cfg.presentmentCurrency,
        locale: this.cfg.locale,
        accessories: (window.nplusAccessories && typeof window.nplusAccessories.getSelection === 'function')
          ? window.nplusAccessories.getSelection() : []
      };
      if (this.cfg.showBilling) {
        out.billing = { address1: val(b.address1), address2: val(b.address2), city: val(b.city), province: val(b.province), zip: val(b.zip), country: val(b.country) };
      }
      if (this.cfg.showProtection) {
        out.protection = { theft: val(p.theft), wear: val(p.wear), inspection: val(p.inspection) };
      }
      return out;
    }

    validate(payload) {
      this.clearFieldErrors();
      var ok = true;
      var req = function (cond, key, msg) { if (cond) { ok = false; this.showFieldError(key, msg || this.t('required', 'Required')); } }.bind(this);
      var c = payload.customer;
      req(!c.firstName, 'firstName'); req(!c.lastName, 'lastName');
      if (!c.email) req(true, 'email'); else if (!EMAIL_RE.test(c.email)) { this.showFieldError('email', this.t('invalid_email', 'Enter a valid email')); ok = false; }
      req(!payload.employer, 'employer');
      if (this.cfg.showBilling) {
        BILLING_REQUIRED.forEach(function (k) { req(!payload.billing[k], k); });
      }
      if (!payload.consent) { this.showFieldError('consent', 'Consent is required to submit.'); ok = false; }
      return ok;
    }

    onSubmit() {
      this.setStatus('');
      this.ensureStripe();
      if (!window.nplusLeasingDeposit) { this.setStatus('Service unavailable. Please refresh.', true); return; }
      var payload = this.collect();
      if (!this.validate(payload)) return;
      if (!this.termsEl || !this.termsEl.checked) { this.setStatus(this.t('accept_terms', 'Please accept the deposit terms.'), true); return; }
      if (!this.stripe || !this.elements) { this.setStatus('Payment is not ready. Please refresh.', true); return; }
      if (!this.cardReady) { this.setStatus(this.t('card_incomplete', 'Please complete your card details.'), true); return; }

      this.setBusy(true);
      var self = this;
      window.nplusLeasingDeposit.createInquiryWithDeposit(payload).then(function (res) {
        return self.elements.submit().then(function (sub) {
          if (sub && sub.error) throw new Error(sub.error.message || 'Card validation failed');
          return self.stripe.confirmPayment({
            elements: self.elements,
            clientSecret: res.clientSecret,
            confirmParams: { return_url: self.confirmationUrl() },
            redirect: 'if_required'
          });
        });
      }).then(function (result) {
        if (result && result.error) throw new Error(result.error.message || 'Payment failed');
        var pi = result && result.paymentIntent;
        if (pi && pi.id) { window.location.assign(self.confirmationUrl() + '?payment_intent=' + encodeURIComponent(pi.id)); return; }
        self.showSuccess();
      }).catch(function (err) {
        self.setStatus(err.message || 'Something went wrong. Please try again.', true);
        self.setBusy(false);
      });
    }

    // Absolute URL of the deposit confirmation page, locale-prefixed (e.g. /de/pages/…).
    confirmationUrl() {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return window.location.origin + root + 'pages/deposit-confirmation';
    }

    showSuccess() {
      var title = this.t('success_title', 'Inquiry & deposit received');
      var body = this.t('success_body', 'Thank you — your deposit is confirmed and your leasing inquiry is in. Our team will be in touch shortly.');
      this.innerHTML = '<div class="nplus-leasing__success"><h3>' + title + '</h3><p>' + body + '</p></div>';
    }
  }

  if (!customElements.get('nplus-leasing-deposit')) {
    customElements.define('nplus-leasing-deposit', NplusLeasingDeposit);
  }
})();
